import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export interface CompanySettings {
  id?: string;
  company_name: string;
  company_address: string;
  company_phone: string;
  company_email: string;
  logo_url: string;
  default_hourly_rate: number;
  default_terms: string;
  default_payment_terms: string;
}

const defaults: CompanySettings = {
  company_name: "",
  company_address: "",
  company_phone: "",
  company_email: "",
  logo_url: "",
  default_hourly_rate: 25,
  default_terms: "Services will be provided as described in this contract. Payment is due within 30 days of invoice date.",
  default_payment_terms: "Net 30",
};

function mapRow(data: any): CompanySettings {
  return {
    id: data.id,
    company_name: data.company_name || "",
    company_address: data.company_address || "",
    company_phone: data.company_phone || "",
    company_email: data.company_email || "",
    logo_url: data.logo_url || "",
    default_hourly_rate: data.default_hourly_rate || 25,
    default_terms: data.default_terms || defaults.default_terms,
    default_payment_terms: data.default_payment_terms || defaults.default_payment_terms,
  };
}

export function useCompanySettings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const queryKey = ["company_settings", user?.id];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!user) return defaults;
      const { data } = await supabase
        .from("company_settings")
        .select("id, company_name, company_address, company_phone, company_email, logo_url, default_hourly_rate, default_terms, default_payment_terms")
        .eq("user_id", user.id)
        .maybeSingle();
      return data ? mapRow(data) : defaults;
    },
    enabled: !!user,
    staleTime: 10 * 60_000,
  });

  // Local override so the form can be edited before save without refetching.
  const [override, setOverride] = useState<CompanySettings | null>(null);
  const settings = override ?? data ?? defaults;
  // Reset override when fresh data arrives from cache miss / refetch.
  useEffect(() => { setOverride(null); }, [data]);

  const setSettings = (next: CompanySettings | ((prev: CompanySettings) => CompanySettings)) => {
    setOverride((prev) => {
      const base = prev ?? data ?? defaults;
      return typeof next === "function" ? (next as any)(base) : next;
    });
  };

  const [isSaving, setIsSaving] = useState(false);

  const saveSettings = async (updated: CompanySettings) => {
    if (!user) return;
    setIsSaving(true);

    const payload = {
      user_id: user.id,
      company_name: updated.company_name || null,
      company_address: updated.company_address || null,
      company_phone: updated.company_phone || null,
      company_email: updated.company_email || null,
      logo_url: updated.logo_url || null,
      default_hourly_rate: updated.default_hourly_rate,
      default_terms: updated.default_terms,
      default_payment_terms: updated.default_payment_terms,
    };

    const { error } = updated.id
      ? await supabase.from("company_settings").update(payload).eq("id", updated.id)
      : await supabase.from("company_settings").insert(payload);

    if (error) {
      toast({ title: "Error saving settings", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Settings saved" });
      setOverride(null);
      await qc.invalidateQueries({ queryKey });
    }
    setIsSaving(false);
  };

  return { settings, setSettings, isLoading, isSaving, saveSettings };
}
